import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useVisitor } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Play, Mail, Building2, User } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

// Block obviously-personal email providers — company emails only
const PERSONAL_EMAIL_DOMAINS = new Set([
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "live.com",
  "icloud.com", "me.com", "aol.com", "protonmail.com", "proton.me",
  "yahoo.co.in", "rediffmail.com", "mail.com", "gmx.com", "zoho.com",
]);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NAME_REGEX = /^[A-Za-z][A-Za-z\s'.-]{1,48}[A-Za-z.]$/;

export default function VisitorGate() {
  const { visitor, loginVisitor, API } = useVisitor();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visitor) navigate("/raw-footage", { replace: true });
  }, [visitor, navigate]);

  const validate = () => {
    const next = {};
    const name = form.name.trim();
    const company = form.company.trim();
    const email = form.email.trim().toLowerCase();
    const phone = (form.phone || "").trim();

    if (!name) next.name = "Name is required";
    else if (name.length < 2) next.name = "Name must be at least 2 characters";
    else if (!NAME_REGEX.test(name)) next.name = "Enter a valid name (letters only)";

    if (!company) next.company = "Company is required";
    else if (company.length < 2) next.company = "Company must be at least 2 characters";

    if (!email) next.email = "Email is required";
    else if (!EMAIL_REGEX.test(email)) next.email = "Enter a valid email";
    else {
      const domain = email.split("@")[1] || "";
      if (PERSONAL_EMAIL_DOMAINS.has(domain)) {
        next.email = "Please use your official company email";
      }
    }

    if (phone && !isValidPhoneNumber(phone)) {
      next.phone = "Enter a valid phone number";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim().toLowerCase(),
        phone: (form.phone || "").trim(),
      };
      const { data } = await axios.post(`${API}/visitors`, payload);
      loginVisitor(data);
      toast.success("Welcome to HiFy Showcase!");
      navigate("/raw-footage");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  return (
    <div className="visitor-gate-bg flex items-center justify-center px-4 py-10" data-testid="visitor-gate">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-6">
            <Play className="w-8 h-8 text-[#FF5A00] fill-[#FF5A00]" />
            <span className="text-3xl font-bold tracking-tight text-[#0A1128]" style={{ fontFamily: "Outfit" }}>
              HiFy
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1128] tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>
            Video Showcase
          </h1>
          <p className="text-[#0A1128]/60 text-base">
            Enter your details to access our proprietary sports content
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-xl p-8 md:p-10 rounded-2xl border border-[#0A1128]/10 shadow-[0_20px_60px_rgba(10,17,40,0.08)]">
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <Field
              id="name"
              label="Full Name *"
              icon={User}
              placeholder="John Doe"
              value={form.name}
              onChange={(v) => updateField("name", v)}
              error={errors.name}
              testId="visitor-name-input"
            />

            <Field
              id="company"
              label="Company *"
              icon={Building2}
              placeholder="Acme Corp"
              value={form.company}
              onChange={(v) => updateField("company", v)}
              error={errors.company}
              testId="visitor-company-input"
            />

            <Field
              id="email"
              label="Official Email *"
              icon={Mail}
              type="email"
              placeholder="john@company.com"
              value={form.email}
              onChange={(v) => updateField("email", v)}
              error={errors.email}
              testId="visitor-email-input"
            />

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-semibold text-[#0A1128]/70 uppercase tracking-wider">
                Phone <span className="text-[#0A1128]/40 normal-case tracking-normal">(optional)</span>
              </Label>
              <PhoneInput
                id="phone"
                international
                defaultCountry="IN"
                placeholder="Enter phone number"
                value={form.phone}
                onChange={(v) => updateField("phone", v || "")}
                data-testid="visitor-phone-input"
                className={`visitor-phone h-12 px-3 rounded-lg bg-[#EAF0EC]/50 border ${
                  errors.phone ? "border-red-400" : "border-[#0A1128]/10"
                } focus-within:border-[#FF5A00] focus-within:ring-2 focus-within:ring-[#FF5A00]/20 transition-colors`}
              />
              {errors.phone && (
                <p className="text-xs text-red-500" data-testid="error-phone">{errors.phone}</p>
              )}
            </div>

            <Button
              type="submit"
              data-testid="visitor-submit-btn"
              disabled={loading}
              className="w-full h-12 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold tracking-wide rounded-lg text-base transition-all shadow-sm mt-2"
            >
              {loading ? "Accessing..." : "Access Showcase"}
            </Button>
          </form>

          <p className="text-center text-xs text-[#0A1128]/40 mt-6">
            Questions?{" "}
            <a href="mailto:harsh.shah@hify.club" className="text-[#FF5A00] hover:underline" data-testid="contact-email-link">
              harsh.shah@hify.club
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, icon: Icon, type = "text", placeholder, value, onChange, error, testId }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-semibold text-[#0A1128]/70 uppercase tracking-wider">
        {label}
      </Label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A1128]/30 pointer-events-none" />
        <Input
          id={id}
          type={type}
          data-testid={testId}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          className={`pl-10 h-12 bg-[#EAF0EC]/50 border-[#0A1128]/10 focus:border-[#FF5A00] focus:ring-[#FF5A00]/20 rounded-lg ${
            error ? "border-red-400 focus:border-red-500" : ""
          }`}
        />
      </div>
      {error && (
        <p className="text-xs text-red-500" data-testid={`error-${id}`}>{error}</p>
      )}
    </div>
  );
}
